import React, { createContext, useState, useRef, useContext } from 'react'
import ConfirmDialog from '../../components/ConfirmDialog';

interface DialogMessages {
  title: string;
  description: string;
  warning?: string;
}

export interface ConfirmDialogContextProps {
  openConfirmation: () => Promise<boolean>;
  setDialogMessages: (messages: DialogMessages) => void;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextProps>({} as ConfirmDialogContextProps);

export const useConfirmDialogContext = () => useContext(ConfirmDialogContext);

export const ConfirmDialogProvider: React.FC = ({children}) => {
  const [opened,setOpened] = useState<boolean>(false);
  const [title,setTitle] = useState<string>('');
  const [description,setDescription] = useState<string>('');
  const [warning,setWarning] = useState<string>('');

  const awaitingPromiseRef = useRef<{
    resolve: (value: boolean) => void;
  }>();

  const openConfirmation = () => {
    setOpened(true);
    return new Promise<boolean>((resolve) => {
      awaitingPromiseRef.current = { resolve };
    });
  };

  const resetDialogMessages = () => {
    setTitle('');
    setDescription('');
    setWarning('');
  }

  const handleClose = () => {
    if (awaitingPromiseRef.current) {
      awaitingPromiseRef.current.resolve(false);
    }

    setOpened(false);
    resetDialogMessages();
  };

  const handleSubmit = () => {
    if (awaitingPromiseRef.current) {
      awaitingPromiseRef.current.resolve(true);
    }
    
    setOpened(false);
    resetDialogMessages();
  };

  const setDialogMessages = (messages: DialogMessages) => {
    setTitle(messages.title);
    setDescription(messages.description);
    if(messages.warning && messages.warning.length > 0) setWarning(messages.warning);
  }

  return (
    <ConfirmDialogContext.Provider value={{openConfirmation, setDialogMessages}}>
      <ConfirmDialog
        opened={opened} 
        onSubmit={handleSubmit}
        onClose={handleClose}
        title={title}
        description={description}
        warning={warning}
      />
      {children}
    </ConfirmDialogContext.Provider>
  );
}

export default ConfirmDialogContext;